import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../hooks/GlobalProvider";
import { useParams, useNavigate } from "react-router-dom";
import { PRODUCT_UPDATE, FetchMedia } from '../../../hooks/APIHooks';

const ProductEdit = () => {
  const { user } = useAuth();
  const { productId } = useParams();
  const [formData, setFormData] = useState({
    product_id: "",
    sku: "",
    product_name: "",
    product_description: "",
    price: "",
    stock_quantity: "",
    product_images: [],
    variants: [],
  });
  const [loading, setLoading] = useState(true);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchProduct();
      fetchMediaFiles();
    }
  }, [user, productId]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${PRODUCT_UPDATE}?product_id=${productId}`);
      if (response.data.success) {
        const productData = response.data.data[0];
        setFormData({
          product_id: productData.product_id,
          sku: productData.sku,
          product_name: productData.product_name,
          product_description: productData.product_description,
          price: productData.price,
          stock_quantity: productData.stock_quantity,
          product_images: [
            productData.product_image,
            productData.product_image_1,
            productData.product_image_2,
            productData.product_image_3,
            productData.product_image_4,
            productData.product_image_5,
          ].filter(Boolean), // Filter out any empty strings
          variants: productData.variants || [],
        });
      } else {
        setError(response.data.error);
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      setError("Error fetching product details.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const fetchMediaFiles = async () => {
    try {
      const response = await axios.get(FetchMedia); // Removed vendor_user_id
      if (response.data.success && Array.isArray(response.data.files)) {
        setMediaFiles(response.data.files); // Set mediaFiles to the files array
      } else {
        console.error("No media files found or response structure is incorrect.");
      }
    } catch (error) {
      console.error("Error fetching media files:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (image) => {
    setFormData((prevState) => {
      const isSelected = prevState.product_images.includes(image);
      const newImages = isSelected
        ? prevState.product_images.filter((img) => img !== image)
        : [...prevState.product_images, image].slice(0, 6); // Limit to 6 images
      return { ...prevState, product_images: newImages };
    });
  };

  const handleVariantChange = (index, e) => {
    const { name, value } = e.target;
    const updatedVariants = [...formData.variants];
    updatedVariants[index] = { ...updatedVariants[index], [name]: value };
    setFormData({ ...formData, variants: updatedVariants });
  };

  const addVariant = () => {
    setFormData({
      ...formData,
      variants: [
        ...formData.variants,
        {
          variant_sku: "",
          variant_option: "",
          variant_price: "",
          variant_stock: "",
        },
      ],
    });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0 || !user) return;

    setUploadLoading(true);
    setMessage("");

    try {
      const uploadPromises = selectedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append("media_file", file);
        // Removed vendor_user_id
        return axios.post(FetchMedia, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      });

      await Promise.all(uploadPromises);
      setMessage("Upload successful");
      fetchMediaFiles(); // Refresh media files after upload
    } catch (error) {
      setMessage("Upload failed");
    } finally {
      setUploadLoading(false);
      setSelectedFiles([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await axios.put(PRODUCT_UPDATE, {
        product_id: formData.product_id,
        sku: formData.sku,
        product_name: formData.product_name,
        product_description: formData.product_description,
        price: parseFloat(formData.price),
        stock_quantity: parseInt(formData.stock_quantity, 10),
        product_image: formData.product_images[0] || null, // First image
        product_image_1: formData.product_images[1] || null,
        product_image_2: formData.product_images[2] || null,
        product_image_3: formData.product_images[3] || null,
        product_image_4: formData.product_images[4] || null,
        product_image_5: formData.product_images[5] || null,
      });

      if (response.data.success) {
        alert("Product updated successfully");
      } else {
        setError(response.data.error);
      }
    } catch (error) {
      console.error("Error updating product:", error);
      setError("Error updating product.");
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Edit Product</h1>
      {error && <p className="text-red-500">{error}</p>}
      {loading ? (
        <p>Loading product details...</p>
      ) : (
        <form onSubmit={handleSubmit} className="mb-6">
          <input type="hidden" name="product_id" value={formData.product_id} />
          <input
            type="text"
            name="sku"
            placeholder="SKU"
            value={formData.sku}
            onChange={handleChange}
            required
            className="border p-2 rounded mb-2 w-full"
          />
          <input
            type="text"
            name="product_name"
            placeholder="Product Name"
            value={formData.product_name}
            onChange={handleChange}
            required
            className="border p-2 rounded mb-2 w-full"
          />
          <textarea
            name="product_description"
            placeholder="Product Description"
            value={formData.product_description}
            onChange={handleChange}
            className="border p-2 rounded mb-2 w-full"
          ></textarea>
          <input
            type="number"
            name="price"
            placeholder="Price"
            value={formData.price}
            onChange={handleChange}
            required
            className="border p-2 rounded mb-2 w-full"
          />
          <input
            type="number"
            name="stock_quantity"
            placeholder="Stock Quantity"
            value={formData.stock_quantity}
            onChange={handleChange}
            required
            className="border p-2 rounded mb-2 w-full"
          />

          <div className="flex flex-col mb-4">
            <label className="text-lg font-semibold mb-2">Select Product Images</label>
            <div className="overflow-x-auto" style={{ maxHeight: "300px" }}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {mediaFiles.map((media) => (
                  <div key={media.file_name} className="relative group">
                    <input
                      type="checkbox"
                      id={media.file_name}
                      value={media.file_name}
                      checked={formData.product_images.includes(media.file_path)}
                      onChange={() => handleImageChange(media.file_path)}
                      className="absolute opacity-0 cursor-pointer"
                    />
                    <label htmlFor={media.file_name} className="cursor-pointer">
                      <div
                        className={`border rounded-lg overflow-hidden transition-transform duration-200 ease-in-out transform group-hover:scale-105 ${
                          formData.product_images.includes(media.file_path) ? "border-primary" : "border-gray-300"
                        }`}
                      >
                        <img
                          src={media.file_path}
                          alt={media.file_name}
                          className="h-32 w-full object-cover"
                        />
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap mt-2">
              {formData.product_images.map((image, index) => (
                image && (
                  <div key={index} className="relative mr-2 mb-2">
                    <img
                      src={image}
                      alt={`Product Image ${index + 1}`}
                      className="w-24 h-24 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={() => handleImageChange(image)} // Remove image on click
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                    >
                      X
                    </button>
                  </div>
                )
              ))}
            </div>
          </div>

          <h3 className="text-lg font-semibold">Variants</h3>
          {formData.variants.map((variant, index) => (
            <div key={index} className="mb-2">
              <input
                type="text"
                name="variant_sku"
                placeholder="Variant SKU"
                value={variant.variant_sku}
                onChange={(e) => handleVariantChange(index, e)}
                required
                className="border p-2 rounded mb-1 w-full"
              />
              <input
                type="text"
                name="variant_option"
                placeholder="Variant Option"
                value={variant.variant_option}
                onChange={(e) => handleVariantChange(index, e)}
                required
                className="border p-2 rounded mb-1 w-full"
              />
              <input
                type="number"
                name="variant_price"
                placeholder="Variant Price"
                value={variant.variant_price}
                onChange={(e) => handleVariantChange(index, e)}
                required
                className="border p-2 rounded mb-1 w-full"
              />
              <input
                type="number"
                name="variant_stock"
                placeholder="Variant Stock"
                value={variant.variant_stock}
                onChange={(e) => handleVariantChange(index, e)}
                required
                className="border p-2 rounded mb-1 w-full"
              />
            </div>
          ))}
          <div className="space-x-2">
            <button
              type="button"
              onClick={addVariant}
              className="bg-primary text-white px-4 py-2 rounded mb-2"
            >
              Add Variant
            </button>
            <button
              type="submit"
              className="bg-primary text-white px-4 py-2 rounded mb-2"
            >
              Update Product
            </button>
          </div>
        </form>
      )}

      <form onSubmit={handleUpload} className="flex items-center space-x-4 mb-6">
        <input type="file" onChange={handleFileChange} className="border p-2 rounded-lg" multiple required />
        <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700" disabled={uploadLoading}>
          {uploadLoading ? "Uploading..." : "Upload"}
        </button>
      </form>
      {message && <p className="text-red-500">{message}</p>}
    </div>
  );
};

export default ProductEdit;
